<script lang="ts" setup>
import type {
  GetRepositoriesForSelectorQuery,
  GetRepositoriesForSelectorQueryVariables,
} from '@/components/SkillsRepositoriesSelector.generated'
import type { SkillsRepositoryInput } from '~/codegen/graphql'
import { GetRepositoriesForSelectorDocument } from '@/components/SkillsRepositoriesSelector.generated'
import { GetTeamRepositoriesListDocument } from './TeamModalRepositories.generated'

interface RepoDisplayInfo {
  id: string
  label: string
  repository_id: string
  mono_repo_config_id?: string
}

function repoKey(r: { repository_id: string; mono_repo_config_id?: string }): string {
  return r.mono_repo_config_id ? `${r.repository_id}:${r.mono_repo_config_id}` : r.repository_id
}

const props = defineProps<{
  teamId: string | undefined
  isGithubSynced: boolean
}>()

const repoEdits = ref<EditSet>(newEditSet())
const reposCache = ref<Map<string, RepoDisplayInfo>>(new Map())

const { data: reposData, isFetching } = useQuery({
  query: GetTeamRepositoriesListDocument,
  variables: computed(() => ({ id: props.teamId ?? '' })),
  paused: () => !props.teamId,
  tags: ['team-repositories'],
  cachePolicy: 'network-only',
})

const isLoading = computed(() => Boolean(props.teamId) && isFetching.value)

const serverRepos = computed<RepoDisplayInfo[]>(() => {
  const repos = reposData.value?.organization?.team?.skillsRepositories ?? []
  return repos.map((r) => {
    const key = r.monoRepoConfigId ? `${r.repositoryId}:${r.monoRepoConfigId}` : r.repositoryId
    return {
      id: key,
      label: `${r.owner}/${r.name}`,
      repository_id: r.repositoryId,
      mono_repo_config_id: r.monoRepoConfigId ?? undefined,
    }
  })
})

const currentRepos = computed<RepoDisplayInfo[]>(() => {
  const serverKeys = new Set(serverRepos.value.map((r) => r.id))
  const result: RepoDisplayInfo[] = serverRepos.value.filter((r) => !repoEdits.value.removed.has(r.id))
  for (const key of repoEdits.value.added) {
    if (serverKeys.has(key)) continue
    const cached = reposCache.value.get(key)
    if (cached) result.push(cached)
  }
  return result
})

const currentRepoKeys = computed<Set<string>>(() => new Set(currentRepos.value.map((r) => r.id)))

const {
  data: repoOptionsData,
  isFetching: isFetchingRepos,
  search: searchRepos,
} = useRelayField<GetRepositoriesForSelectorQuery, GetRepositoriesForSelectorQueryVariables, SelectMenuOption[]>(
  GetRepositoriesForSelectorDocument,
  computed(() => ({ first: 50, term: '', existing: currentRepos.value.map((r) => r.repository_id) })),
  ({ data }) => {
    const repositories = getListOfNodes(data?.organization.repositories).filter((node) => node.id != null)
    const options: SelectMenuOption[] = []

    for (const repo of repositories) {
      const repoLabel = `${repo.owner}/${repo.name}`
      reposCache.value.set(repo.id!, {
        id: repo.id!,
        label: repoLabel,
        repository_id: repo.id!,
        mono_repo_config_id: undefined,
      })
      if (!currentRepoKeys.value.has(repo.id!)) {
        options.push({ label: repoLabel, value: repo.id! })
      }
      const monoConfigs = repo.monoRepoConfigs ?? []
      for (const config of monoConfigs) {
        const key = `${repo.id}:${config.id}`
        const monoLabel = `${repoLabel} (${config.name})`
        reposCache.value.set(key, {
          id: key,
          label: monoLabel,
          repository_id: repo.id!,
          mono_repo_config_id: config.id ?? undefined,
        })
        if (!currentRepoKeys.value.has(key)) {
          options.push({ label: monoLabel, value: key })
        }
      }
    }
    return options
  },
)

const availableRepoOptions = computed<SelectMenuOption[]>(() => repoOptionsData.value ?? [])

function addRepo(compositeValue: string) {
  if (!compositeValue || currentRepoKeys.value.has(compositeValue)) return
  if (repoEdits.value.removed.has(compositeValue)) {
    repoEdits.value.removed.delete(compositeValue)
  } else {
    repoEdits.value.added.add(compositeValue)
  }
}

function removeRepo(repo: RepoDisplayInfo) {
  if (repoEdits.value.added.has(repo.id)) {
    repoEdits.value.added.delete(repo.id)
  } else {
    repoEdits.value.removed.add(repo.id)
  }
}

function reset() {
  repoEdits.value = newEditSet()
}

function getFinalRepos(): SkillsRepositoryInput[] {
  return currentRepos.value.map((r) => {
    const input: SkillsRepositoryInput = { repositoryId: r.repository_id }
    if (r.mono_repo_config_id !== undefined) {
      input.monoRepoConfigId = r.mono_repo_config_id
    }
    return input
  })
}

defineExpose({
  getFinalRepos,
  reset,
  isLoading,
})
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="mb-4 text-lg font-semibold">Repositories</div>

    <div v-if="currentRepos.length > 0" class="min-h-0 flex-1 overflow-y-auto">
      <div v-for="repo in currentRepos" :key="repo.id" class="flex items-center justify-between py-2 pr-3">
        <div class="flex items-center gap-2.5">
          <UIcon name="light:code-branch" class="size-5 text-gray-500" />
          <span class="text-sm font-medium text-gray-900 dark:text-white">{{ repo.label }}</span>
        </div>
        <UTooltip
          :text="SYNC_DISABLED_TOOLTIP"
          :prevent="!isGithubSynced"
          :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
        >
          <UButton
            icon="light:xmark"
            variant="ghost"
            color="gray"
            size="xs"
            :disabled="isGithubSynced"
            @click="removeRepo(repo)"
          />
        </UTooltip>
      </div>
    </div>
    <div v-else class="flex-1 text-sm text-gray-500">No repositories added yet.</div>

    <UTooltip
      :text="SYNC_DISABLED_TOOLTIP"
      :prevent="!isGithubSynced"
      class="mt-4 block"
      :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
    >
      <SelectMenu
        :model-value="null"
        :is-fetching="isFetchingRepos"
        :options="availableRepoOptions"
        :disabled="isGithubSynced"
        placeholder="Add a repository..."
        leading-icon="light:magnifying-glass"
        option-attribute="label"
        value-attribute="value"
        size="md"
        searchable
        @update:query="searchRepos"
        @update:model-value="addRepo"
      />
    </UTooltip>
  </div>
</template>
